/**
 * CasaneoAuthContext — session for our OWN backend (FastAPI + JWT cookies).
 *
 * Independent of `AuthContext.jsx`, which still handles the legacy external
 * `rental-hub-manager.emergent.host` bearer session. We run them side-by-side
 * during the strangler migration (see /app/docs/MIGRATION.md Phase 5).
 *
 * States (`casaneoUser`):
 *   – `undefined` — session probe in flight
 *   – object      — authenticated Casanéo user
 *   – `null`      — not authenticated
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { localApi } from "../lib/localApi";
import { hydrateTenantSettings } from "../lib/tenantSettings";

const Ctx = createContext(null);

export function CasaneoAuthProvider({ children }) {
  const [casaneoUser, setCasaneoUser] = useState(undefined);

  const check = useCallback(async () => {
    // Skip if we're in the middle of a Google callback — CasaneoAuthCallback
    // will populate the state after the session_id exchange.
    if (typeof window !== "undefined" && window.location.hash.includes("casaneo_session_id=")) {
      return;
    }
    try {
      const { data } = await localApi.get("/auth/me");
      setCasaneoUser(data);
      // Hydrate the per-property settings (weekend rules, seasons, checkin
      // days, property types) from the server so localStorage stays in sync
      // across devices. Runs once per boot, silent on failure.
      hydrateTenantSettings().catch(() => { /* noop */ });
    } catch {
      setCasaneoUser(null);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const login = useCallback(async (email, password) => {
    const { data } = await localApi.post("/auth/login", { email, password });
    setCasaneoUser(data.user);
    hydrateTenantSettings().catch(() => { /* noop */ });
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await localApi.post("/auth/register", { email, password, name });
    setCasaneoUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await localApi.post("/auth/logout"); } catch { /* noop */ }
    setCasaneoUser(null);
  }, []);

  const exchangeGoogleSession = useCallback(async (sessionId) => {
    const { data } = await localApi.post("/auth/google/session", { session_id: sessionId });
    setCasaneoUser(data.user);
    return data.user;
  }, []);

  const changePassword = useCallback(async (current_password, new_password) => {
    await localApi.post("/auth/change-password", { current_password, new_password });
  }, []);

  return (
    <Ctx.Provider
      value={{
        casaneoUser,
        casaneoLoading: casaneoUser === undefined,
        login,
        register,
        logout,
        exchangeGoogleSession,
        changePassword,
        refresh: check,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useCasaneoAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useCasaneoAuth must be used within CasaneoAuthProvider");
  return ctx;
}
